import type * as React from 'react'
import { createContext, forwardRef, useContext, useId, useMemo } from 'react'

import { clsx } from 'clsx'

import {
  Button,
  Input,
  Label as PopcornLabel,
  NewBadge as PopcornNewBadge,
  Text,
  Slot,
  Separator,
} from '@buffer-mono/popcorn'
import type {
  InputProps as PopcornInputProps,
  LabelProps as PopcornLabelProps,
  NewBadgeProps as PopcornNewBadgeProps,
} from '@buffer-mono/popcorn'

import styles from './ChannelFields.module.css'

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.Root
 * The root container for the channel fields section.
 * -----------------------------------------------------------------------------------------------*/

type RootProps = React.HTMLAttributes<HTMLElement> & {
  separator?: boolean
}

function Root({
  className,
  separator = true,
  ...props
}: RootProps): React.ReactElement {
  if (separator) {
    return (
      <>
        <div className={styles.separatorContainer}>
          <Separator className={styles.separator} />
        </div>
        <section className={clsx(styles.channelFields, className)} {...props} />
      </>
    )
  }
  return (
    <section className={clsx(styles.channelFields, className)} {...props} />
  )
}

Root.displayName = 'ChannelFields.Root'

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.Field Context
 * Provides id to Label and Control for automatic htmlFor/id association.
 * -----------------------------------------------------------------------------------------------*/

type FieldContextValue = {
  id: string
}

const FieldContext = createContext<FieldContextValue | null>(null)

function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext)
}

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.Field
 * A wrapper for individual channel fields with enter/exit animations.
 * Accepts children for flexible content composition.
 * -----------------------------------------------------------------------------------------------*/

type FieldProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Whether the field is in an exiting state (triggers exit animation)
   * @default false
   */
  isExiting?: boolean
}

function Field({
  id,
  isExiting = false,
  className,
  children,
  ...props
}: FieldProps): React.ReactElement {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const contextValue = useMemo(() => ({ id: fieldId }), [fieldId])

  return (
    <FieldContext.Provider value={contextValue}>
      <div
        className={clsx(
          styles.field,
          isExiting && styles.fieldExiting,
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </FieldContext.Provider>
  )
}

Field.displayName = 'ChannelFields.Field'

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.Label
 * A wrapper for the Popcorn Label component with channel field styling.
 * Automatically associates with the field's control via htmlFor from context.
 * -----------------------------------------------------------------------------------------------*/

type LabelProps = PopcornLabelProps

function Label({
  className,
  htmlFor,
  ...props
}: LabelProps): React.ReactElement {
  const fieldContext = useFieldContext()
  const resolvedHtmlFor = htmlFor ?? fieldContext?.id

  return (
    <PopcornLabel
      className={clsx(styles.label, className)}
      htmlFor={resolvedHtmlFor}
      {...props}
    />
  )
}

Label.displayName = 'ChannelFields.Label'

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.NewBadge
 * A New badge for the start of a Label. It hangs into the space left of the
 * label so labels stay aligned across fields, and sits on the label's baseline.
 * -----------------------------------------------------------------------------------------------*/

function NewBadge(props: PopcornNewBadgeProps): React.ReactElement {
  return (
    <span className={styles.newBadgeAnchor} aria-hidden="true">
      <PopcornNewBadge {...props} />
    </span>
  )
}

NewBadge.displayName = 'ChannelFields.NewBadge'

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.Control
 * A container for the field's input or content.
 * Automatically receives id from field context for label association.
 * -----------------------------------------------------------------------------------------------*/

type ControlProps =
  | {
      /**
       * Render the control as a child element
       */
      asChild: true
      children: React.ReactNode
      className?: string
      id?: string
    }
  | ({
      /**
       * Render as an Input
       */
      asChild?: false
    } & PopcornInputProps)

const Control = forwardRef<HTMLInputElement, ControlProps>(function Control(
  { asChild, className, children, id, ...inputProps },
  ref,
) {
  const fieldContext = useFieldContext()
  const resolvedId = id ?? fieldContext?.id

  if (asChild) {
    return (
      <Slot.Root
        ref={ref}
        className={clsx(styles.control, className)}
        id={resolvedId}
      >
        {children}
      </Slot.Root>
    )
  }

  return (
    <Input
      ref={ref}
      className={clsx(styles.control, className)}
      id={resolvedId}
      {...inputProps}
    />
  )
})

Control.displayName = 'ChannelFields.Control'

type LockedControlProps = {
  children: React.ReactNode
  indicator: React.ReactNode
  indicatorPlacement?: 'inline' | 'end'
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}

function LockedControl({
  children,
  indicator,
  indicatorPlacement = 'inline',
  onClick,
}: LockedControlProps): React.ReactElement {
  return (
    <Control asChild>
      <Button
        variant="secondary"
        className={clsx(
          styles.lockedControl,
          indicatorPlacement === 'end' && styles.lockedControlEnd,
        )}
        onClick={onClick}
      >
        {indicatorPlacement === 'end' ? (
          <>
            <span className={styles.lockedText}>{children}</span>
            <span className={styles.lockedIndicatorEnd}>{indicator}</span>
          </>
        ) : (
          <>
            {children}
            {indicator}
          </>
        )}
      </Button>
    </Control>
  )
}

LockedControl.displayName = 'ChannelFields.LockedControl'

/* -------------------------------------------------------------------------------------------------
 * ChannelFields.CharacterCounter
 * A wrapper for the CharacterCounter component with channel field styling.
 * -----------------------------------------------------------------------------------------------*/

type CharacterCounterProps = React.ComponentProps<typeof Text> & {
  maxCount: number
  count: number
}

function CharacterCounter({
  count,
  maxCount,
  className,
  ...props
}: CharacterCounterProps): React.ReactElement {
  const left = maxCount - count
  const isAboveMax = left < 0
  return (
    <Text
      className={clsx(styles.characterCounter, className)}
      aria-invalid={isAboveMax}
      {...props}
    >
      {left}
    </Text>
  )
}

CharacterCounter.displayName = 'ChannelFields.CharacterCounter'

/* -------------------------------------------------------------------------------------------------
 * Export
 * -----------------------------------------------------------------------------------------------*/

const ChannelFields = Object.assign(Root, {
  Root,
  Field,
  Label,
  NewBadge,
  Control,
  LockedControl,
  CharacterCounter,
})

export { ChannelFields }
export type {
  RootProps as ChannelFieldsRootProps,
  FieldProps as ChannelFieldsFieldProps,
  LabelProps as ChannelFieldsLabelProps,
  ControlProps as ChannelFieldsControlProps,
  LockedControlProps as ChannelFieldsLockedControlProps,
  CharacterCounterProps as ChannelFieldsCharacterCounterProps,
}
